import { SplitViewContext } from "../../contexts";
import { useEffect, useRef, useState } from "react";

export default function SplitView({
    id,
    children,
    hideLeftPane = false,
    hideRightPane = false,
    dividerWidth = 16,
    primary = "right",
    useRatio = true,
    defaultSplitRatio = 50,
    minRatio = 25,
    maxRatio = 75,
    defaultSize = 434,
    minWidth = 275,
    maxWidth = 900,
}) {
    const splitValueStorageKey = `split_value_${id}`;
    const halfDividerWidth = dividerWidth / 2;
    const containerRef = useRef();
    const leftPaneRef = useRef();
    const rightPaneRef = useRef();
    const [isResizing, setIsResizing] = useState(false);
    const [leftPaneWidth, setLeftPaneWidth] = useState();
    const [rightPaneWidth, setRightPaneWidth] = useState();

    const handleMouseDown = (e) => {
        e.preventDefault();
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
        setIsResizing(true);
    };

    const handleMouseUp = (e) => {
        e.preventDefault();
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        setIsResizing(false);
        saveSplitValueToStorage(e);
        setPanesWidth();
    };

    const handleMouseMove = (e) => {
        e.preventDefault();
        if (primary === "right") {
            rightPaneRef.current.style.width = useRatio ? `calc(${getRightPaneRatio(e)}% - 8px)` : `${getRightPaneWidth(e)}px`;
        } else {
            leftPaneRef.current.style.width = useRatio ? `calc(${getLeftPaneRatio(e)}% - 8px)` : `${getLeftPaneWidth(e)}px`;
        }
    };

    const checkWidth = (width) => {
        let newWidth = width;
        if (newWidth < minWidth) newWidth = minWidth;
        if (newWidth > maxWidth) newWidth = maxWidth;
        return newWidth;
    }

    const getLeftPaneWidth = (e) => {
        let newWidth = e.clientX - leftPaneRef.current.getBoundingClientRect().left - halfDividerWidth;
        return checkWidth(newWidth);
    };

    const getRightPaneWidth = (e) => {
        let newWidth = rightPaneRef.current.getBoundingClientRect().right - e.clientX - halfDividerWidth;
        return checkWidth(newWidth);
    };

    const checkRatio = (ratio) => {
        let newRatio = ratio;
        if (newRatio < minRatio) newRatio = minRatio;
        if (newRatio > maxRatio) newRatio = maxRatio;
        return newRatio;
    }

    const getLeftPaneRatio = (e) => {
        let newRatio = (e.clientX - leftPaneRef.current.getBoundingClientRect().left) / containerRef.current.getBoundingClientRect().width * 100;
        return checkRatio(newRatio);
    }

    const getRightPaneRatio = (e) => {
        let newRatio = (rightPaneRef.current.getBoundingClientRect().right - e.clientX) / containerRef.current.getBoundingClientRect().width * 100;
        return checkRatio(newRatio);
    }

    const saveSplitValueToStorage = (e) => {
        if (primary === "right") {
            localStorage.setItem(splitValueStorageKey, JSON.stringify({ ratio: getRightPaneRatio(e), width: getRightPaneWidth(e) }));
        } else {
            localStorage.setItem(splitValueStorageKey, JSON.stringify({ ratio: getLeftPaneRatio(e), width: getLeftPaneWidth(e) }));
        }
    }

    const getSplitValueFromStorage = () => {
        const saved = localStorage.getItem(splitValueStorageKey);
        if (saved) {
            return JSON.parse(saved);
        } else {
            return { ratio: defaultSplitRatio, width: defaultSize };
        }
    }

    const setPanesWidth = () => {
        setLeftPaneWidth(leftPaneRef.current?.getBoundingClientRect().width);
        setRightPaneWidth(rightPaneRef.current?.getBoundingClientRect().width);
    }

    const resetPaneWidth = () => {
        // When it hides the left pane, it resets the width of the right pane
        if (hideLeftPane) {
            if (rightPaneRef.current)
                rightPaneRef.current.style.width = "100%";
            setPanesWidth();
            return;
        } else {
            if (rightPaneRef.current)
                rightPaneRef.current.style.width = "";
        }

        // When it hides the right pane, it resets the width of the left pane
        if (hideRightPane) {
            if (leftPaneRef.current)
                leftPaneRef.current.style.width = "100%";
            setPanesWidth();
            return;
        } else {
            if (leftPaneRef.current)
                leftPaneRef.current.style.width = "";
        }

        // It gets split size preference from local storage or uses the default value
        const { ratio, width } = getSplitValueFromStorage();
        const newWidth = useRatio ? `calc(${ratio}% - 8px)` : `${width}px`;
        if (primary === "right") {
            rightPaneRef.current.style.width = newWidth;
        } else {
            leftPaneRef.current.style.width = newWidth;
        }

        setPanesWidth();
    }

    useEffect(() => {
        resetPaneWidth();
    }, [hideLeftPane, hideRightPane])

    return (
        <SplitViewContext.Provider value={{
            leftPaneWidth: leftPaneWidth,
            rightPaneWidth: rightPaneWidth,
        }}>
            <div
                id={id}
                ref={containerRef}
                className={`tw-relative tw-w-full tw-h-full ${!hideLeftPane ? "tw-flex tw-flex-row" : ""}`}
            >
                {/* Left Pane */}
                {!hideLeftPane && (
                    <div
                        ref={leftPaneRef}
                        className={`tw-h-full ${primary === "right" ? "tw-flex-1" : ""}`}
                    >
                        {children[0]}
                    </div>
                )}

                {/* Divider */}
                {!(hideLeftPane || hideRightPane) && (
                    <div
                        className="tw-h-full"
                        style={{
                            cursor: 'col-resize',
                            width: `${dividerWidth}px`,
                        }}
                        onMouseDown={handleMouseDown}
                    ></div>
                )}

                {/* Right Pane */}
                {!hideRightPane && (
                    <div
                        ref={rightPaneRef}
                        className={`tw-h-full ${primary === "right" ? "" : "tw-flex-1"}`}
                    >
                        {children[1]}
                    </div>
                )}

                {/* Overlay when resizing */}
                {isResizing && (
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            cursor: "col-resize",
                            zIndex: 9999,
                            width: "100%",
                        }}
                    />
                )}
            </div>
        </SplitViewContext.Provider>
    )

}
